/**
 * monday's `dynamicMapping` — the `itemMapping` / `pulseMapping` / `eventMapping` / `boardMapping`
 * slot the cross-board creators and the calendar block carry
 * (`recipe-catalogue-static.json` `configurationFields.pulseMapping.type === 'dynamicMapping'`).
 *
 * Shape, read off the real configs of blocks `15499`, `5826`, `6899`, `496691` and recipes
 * `225` / `223` / `6001`:
 *
 * ```jsonc
 * { "groupId": "topics",
 *   "mappingConfig": {
 *     "<targetColumnId>": {
 *       "targetField": { "id", "columnId", "title", "type", "boardId", "outboundType", "inboundTypes" },
 *       "mappingParts": [ {"type":"text","text":"Deal "},
 *                         {"type":"field","sourceField":{"id","columnId","title","type","boardId"}} ] } },
 *   "mappingDefinition": { … } }
 * ```
 *
 * A target cell is therefore a **concatenation** of literal text and source-cell *text*, which
 * is exactly why the write goes back in through the column type's `simple` form: monday's
 * `outboundTypesMap` (captured verbatim in the same config) reduces every column to one of
 * `text | text_array | numeric | date_time | boolean | user_email(s)`, i.e. to the display text
 * the simple form already parses. Nothing here interprets a column value.
 *
 * `sourceField.type` values that are not board columns are resolved from the run instead:
 * `pulseId`, `name`, `boardName`, `group`. Anything else (the Emails & Activities fields of
 * block `496691`: `source_item`, `activityType`, `endDate`, …) has no source in our model and
 * renders empty, which is why `496691` is registered but not executable.
 */
import { z } from 'zod';
import { interpolateTokens } from './tokens.js';
import { tokenContextFor } from './blocks/token-context.js';
export const mappingFieldSchema = z.looseObject({
    id: z.string().optional(),
    uuid: z.string().optional(),
    columnId: z.string().optional(),
    title: z.string().optional(),
    type: z.string().optional(),
    boardId: z.union([z.number(), z.string()]).optional(),
    outboundType: z.string().optional(),
});
export const mappingPartSchema = z.union([
    z.looseObject({ type: z.literal('text'), text: z.string().default('') }),
    z.looseObject({ type: z.literal('field'), sourceField: mappingFieldSchema }),
]);
export const mappingEntrySchema = z.looseObject({
    targetField: mappingFieldSchema.optional(),
    mappingParts: z.array(mappingPartSchema).default([]),
});
export const itemMappingSchema = z
    .looseObject({
    groupId: z.string().nullable().default(null),
    mappingConfig: z.record(z.string(), mappingEntrySchema).default({}),
    invalid: z.boolean().optional(),
})
    .default({ groupId: null, mappingConfig: {} });
/** `88520`'s slot, verbatim from automation 127241238 (the only production use). */
export const boardMappingSchema = z.looseObject({
    boardKind: z.string().default('public'),
    templateId: z.union([z.number(), z.string()]).nullable().default(null),
    boardName: z.string().default(''),
    folderId: z.union([z.number(), z.string()]).nullable().default(null),
    workspaceId: z.union([z.number(), z.string()]).nullable().default(null),
    boardOwners: z.array(z.union([z.number(), z.string()])).default([]),
    boardOwnerTeams: z.array(z.union([z.number(), z.string()])).default([]),
    boardSubscribers: z.array(z.union([z.number(), z.string()])).default([]),
    boardTeamsSubscribers: z.array(z.union([z.number(), z.string()])).default([]),
    invalid: z.boolean().optional(),
});
/** The column a `mappingConfig` key targets. The key is the target column id; `targetField`
 *  repeats it and is preferred when present (both agree in every production config). */
const targetColumnOf = (key, entry) => entry.targetField?.columnId ?? entry.targetField?.id ?? key;
/** Every source column id the mapping reads, so `references()` can report them. */
export function mappingSourceColumns(mapping) {
    const ids = new Set();
    for (const entry of Object.values(mapping.mappingConfig)) {
        for (const part of entry.mappingParts) {
            if (part.type !== 'field')
                continue;
            const field = part.sourceField;
            if (field.type === 'name' || field.type === 'pulseId' || field.type === 'boardName' || field.type === 'group')
                continue;
            if (typeof field.columnId === 'string')
                ids.add(field.columnId);
        }
    }
    return [...ids];
}
export function mappingTargetColumns(mapping) {
    return Object.entries(mapping.mappingConfig).map(([key, entry]) => targetColumnOf(key, entry));
}
/**
 * Renders a mapping against the triggering item. Text parts are token-interpolated first, so a
 * literal `{pulse.name}` inside a mapping part behaves exactly as it does in a message (§5.7).
 */
export async function resolveItemMapping(context, mapping) {
    const literals = Object.values(mapping.mappingConfig).flatMap((entry) => entry.mappingParts.filter((part) => part.type === 'text').map((part) => part.text));
    const tokens = await tokenContextFor(context, literals);
    const item = context.itemId === null ? null : await context.services.getItem(context.itemId);
    const board = await context.services.getBoard(context.boardId);
    const unresolved = new Set();
    const partText = async (part) => {
        if (part.type === 'text')
            return interpolateTokens(part.text, tokens);
        const field = part.sourceField;
        switch (field.type) {
            case 'name':
                return item?.name ?? '';
            case 'pulseId':
                return context.itemId ?? '';
            case 'boardName':
                return board?.name ?? '';
            case 'group':
                return item?.groupId ?? '';
            default:
                break;
        }
        if (typeof field.columnId === 'string' && context.itemId !== null) {
            return (await context.services.getCellText(context.itemId, field.columnId)) ?? '';
        }
        unresolved.add(field.id ?? field.type ?? 'unknown');
        return '';
    };
    const render = async (parts) => {
        const pieces = [];
        for (const part of parts)
            pieces.push(await partText(part));
        return pieces.join('');
    };
    let name = null;
    const cells = [];
    for (const [key, entry] of Object.entries(mapping.mappingConfig)) {
        const columnId = targetColumnOf(key, entry);
        const text = await render(entry.mappingParts);
        if (columnId === 'name') {
            name = text;
            continue;
        }
        cells.push({ columnId, text });
    }
    return { name, groupId: mapping.groupId, cells, unresolved: [...unresolved] };
}
/**
 * Writes a resolved mapping onto a freshly created item. Each cell goes through
 * `setCellValue(form: 'simple')`, i.e. the same path as `/v2`'s
 * `change_simple_column_value` — so an unwritable text (a label that does not exist on the
 * target board, say) fails that one cell instead of the whole action, exactly as monday's
 * mapping does.
 */
export async function applyMappedCells(context, target, cells) {
    const written = [];
    const failed = [];
    for (const cell of cells) {
        if (cell.text.length === 0)
            continue;
        try {
            await context.services.setCellValue({
                boardId: target.boardId,
                itemId: target.itemId,
                columnId: cell.columnId,
                value: cell.text,
                form: 'simple',
            });
            written.push(cell.columnId);
        }
        catch (error) {
            failed.push({ columnId: cell.columnId, reason: error instanceof Error ? error.message : String(error) });
        }
    }
    return { written, failed };
}
//# sourceMappingURL=item-mapping.js.map